import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { Field, FieldArray } from 'redux-form';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import Tooltip from '@omilia/tooltip';

import uuid from 'uuid/v4';

import { entityType } from 'types';

import Toggler from 'components/Toggler';
import InspectHeader from 'components/InspectHeader';
import { FormError } from 'components/Form';

import FormGroupAutoCompleteField from 'components/AutoCompleteMenuWrapper';

import './style.scss';

const createRemoveHandler = (fields, onDelete, index) => () => {
  onDelete(fields.get(index).id);
  fields.remove(index);
};

const SortableMatchingPattern = SortableElement(
  ({ fields, field, sortIndex, isArchived, onDelete }) => {
    const { name, color, mandatory, archived } = fields.get(sortIndex);

    return (
      <div
        styleName={cn('matching-pattern', {
          'is-rule-archived': isArchived,
          'is-archived': archived,
          'is-optional': !mandatory,
        })}
      >
        <div styleName="marker" style={{ backgroundColor: color }} />

        <div
          styleName={cn('name', {
            'is-archived': archived,
          })}
        >
          {sortIndex + 1} - {name}
        </div>

        <Tooltip
          placement="right"
          content={`${mandatory ? 'mandatory' : 'optional'} entity`}
        >
          <div styleName="toggler">
            <Field
              component={Toggler}
              name={`${field}.mandatory`}
              isDisabled={isArchived}
            />
          </div>
        </Tooltip>

        <div styleName="cross">
          <button
            data-test="delete-matching-pattern"
            type="button"
            className="icon-close"
            disabled={isArchived}
            onClick={createRemoveHandler(fields, onDelete, sortIndex)}
          />
        </div>
      </div>
    );
  },
);

const SortableList = SortableContainer(({ fields, isArchived, onDelete }) => (
  <div styleName={cn('list', { 'is-archived': isArchived })}>
    {fields.map((field, index) => (
      <SortableMatchingPattern
        key={field}
        index={index}
        sortIndex={index}
        field={field}
        fields={fields}
        isArchived={isArchived}
        onDelete={onDelete}
      />
    ))}
  </div>
));

export default class MatchingPatternTab extends Component {
  static propTypes = {
    allEntities: PropTypes.arrayOf(entityType),
    isArchived: PropTypes.bool,
    isLoading: PropTypes.bool,
    hasMandatory: PropTypes.bool,

    onDelete: PropTypes.func.isRequired,
    onSearchEntity: PropTypes.func.isRequired,

    // REDUX-FORM:
    change: PropTypes.func.isRequired,
  };

  state = {
    movingMatchingPattern: null,
  };

  createSelectHandler = fields => item => {
    // FIXME: item always contains preventDefault method, payload and event's methods should be split
    if (item.id) {
      fields.push({
        id: uuid(),
        entity: item.id,
        mandatory: true,
        order: fields.length,
        name: item.name,
        color: item.color,
        features: item.features,
      });
    }
  };

  createSortHandler = fields => ({ oldIndex, newIndex }) => {
    if (oldIndex !== newIndex) {
      fields.move(oldIndex, newIndex);
    }
  };

  handleSelectItem = () => {
    // reset the value of the field after item selection
    this.props.change('matching-pattern', '');
  };

  handleShouldCancelStart = () => this.props.isArchived;

  renderList = ({ fields }) => {
    const {
      allEntities,
      isArchived,
      isLoading,
      onSearchEntity,
      onDelete,
      hasMandatory,
    } = this.props;
    const { movingMatchingPattern } = this.state;

    return (
      <div styleName="root">
        <InspectHeader />
        <div styleName="inner">
          {!hasMandatory && (
            <FormError>Make at least one matching pattern mandatory</FormError>
          )}

          {fields.length > 0 && (
            <SortableList
              data-test="sortable-list"
              fields={fields}
              isArchived={isArchived}
              axis="xy"
              lockToContainerEdges
              helperClass="draging-matching-pattern"
              movingIndex={movingMatchingPattern}
              onDelete={onDelete}
              onSortEnd={this.createSortHandler(fields)}
              shouldCancelStart={this.handleShouldCancelStart}
              distance={1}
            />
          )}

          <div styleName="dropdown">
            <Field
              autoFocus={!isArchived}
              data-test="add-matching-pattern"
              component={FormGroupAutoCompleteField}
              name="matching-pattern"
              label="MATCHING PATTERN"
              styleName="group-field"
              placeholder="Select entities"
              items={allEntities}
              isDisabled={isArchived}
              isLoading={isLoading}
              onChange={this.createSelectHandler(fields)}
              onOpen={onSearchEntity}
              onChangeValue={onSearchEntity}
              selectItem={this.handleSelectItem}
            />
          </div>
        </div>
      </div>
    );
  };

  render() {
    return <FieldArray name="entities" component={this.renderList} />;
  }
}
